// Import RELATIVO a propósito (T7-g): el script de reconciliación importa
// esta hoja desde node (dynamic import), y node no resuelve el alias @core
// de vite. Mismo archivo, mismo símbolo — solo cambia cómo se llega.
import { claveDiaLocal } from '../../../core/lib/diaLocal.js'
// ═══════════════════════════════════════════════════════════════════════════
// kpisVentas — KPIs de ventas sobre órdenes canónicas (PURO)
// ───────────────────────────────────────────────────────────────────────────
// Extraído VERBATIM de VentasPage.calcKpis (PR-2a del plan Cuadro General,
// regla anti-drift §6.4): todo número que muestran dos vistas se calcula en
// UNA sola función que ambas importan — VentasPage y el Cuadro General citan
// exactamente los mismos KPIs por construcción (regla fundacional §10).
//
// También viven acá los predicados canónicos de estado (antes en
// hooks/useOrdenesPeriodo.js, que ahora los RE-exporta): son la regla
// R-status sobre `estado` canónico y no dependen de firebase.
//
// Sin imports de firebase/react: testeable con vitest y con harness node.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Predicados canónicos (regla R-status sobre `estado`) ───────────────────
// `estado` canónico (ordenesShape.normalizarEstado):
//   paid → pagada · confirmed → confirmada · cancelled|invalid → cancelada
//   payment_required|payment_in_process|partially_paid → pendiente
//   desconocido → null (fail-loud)
export const ESTADOS_FACTURABLES = ['pagada', 'confirmada']
export const ESTADOS_CANCELADOS = ['cancelada']
export const esFacturable = (o) => ESTADOS_FACTURABLES.includes(o?.estado)
export const esCancelado = (o) => ESTADOS_CANCELADOS.includes(o?.estado)

// ─── Helpers ────────────────────────────────────────────────────────────────

function toDate(ts) {
  if (!ts) return null
  const d = ts.toDate ? ts.toDate() : new Date(ts)
  if (isNaN(d.getTime())) return null
  return d
}

// Una cantidad que no es número (null, '', basura) no suma: ni NaN ni un 1 inventado.
function cantidadDe(it) {
  const c = it?.cantidad
  if (c == null) return 0
  const n = Number(c)  // '' → 0: tampoco suma
  return Number.isFinite(n) ? n : 0
}

/**
 * Las unidades vendidas: la suma de las cantidades de los productos de las
 * ventas FACTURABLES (el mismo conjunto que "Pagadas" y "Facturado"),
 * para cualquier canal (ML y TN comparten el shape canónico de `items`). Una
 * venta facturable sin productos en el doc no suma cero a escondidas: se
 * cuenta en `ordenesSinItems`, y con alguna así la tendencia de unidades no
 * se afirma.
 */
export function unidadesVendidas(ordenes) {
  let unidades = 0
  let ordenesSinItems = 0
  for (const o of ordenes || []) {
    if (!esFacturable(o)) continue
    const items = Array.isArray(o?.items) ? o.items : []
    if (items.length === 0) { ordenesSinItems++; continue }
    for (const it of items) unidades += cantidadDe(it)
  }
  return { unidades, ordenesSinItems }
}

// ─── Cálculo de KPIs ────────────────────────────────────────────────────────

/**
 * Recibe un array de órdenes canónicas y devuelve el set de métricas del
 * dashboard Ventas. El caller lo corre sobre el período actual y el anterior
 * para armar deltas.
 *
 * @param {Array} orders — órdenes canónicas (shape de useOrdenesPeriodo)
 */
export function calcKpisVentas(orders) {
  const total = orders.length
  // "Pagadas" / "Facturado" = facturables sobre `estado` canónico
  // (pagada + confirmada). El estado ya viene normalizado del adapter —
  // shipped/delivered del legacy mapean a `pagada` en la canónica.
  const pagadas = orders.filter(esFacturable)
  const canceladas = orders.filter(esCancelado)
  const totalMonto = orders.reduce((s, o) => s + (o.montoTotal || 0), 0)
  const montoPagadas = pagadas.reduce((s, o) => s + (o.montoTotal || 0), 0)
  const montoCanceladas = canceladas.reduce((s, o) => s + (o.montoTotal || 0), 0)
  const ticket = pagadas.length > 0 ? montoPagadas / pagadas.length : 0
  const conversion = total > 0 ? pagadas.length / total : 0
  const tasaCancelacion = total > 0 ? canceladas.length / total : 0

  // Velocidad de pago (horas entre fechaCreada y fechaCerrada para pagadas)
  const tiemposPago = []
  for (const o of pagadas) {
    const created = toDate(o.fechaCreada)
    const closed = toDate(o.fechaCerrada) || toDate(o.fechaModifCanal)
    if (created && closed) {
      const horas = (closed - created) / 3600000
      if (horas >= 0 && horas < 24 * 30) tiemposPago.push(horas)
    }
  }
  const velocidadPagoHoras = tiemposPago.length > 0
    ? tiemposPago.reduce((a, b) => a + b, 0) / tiemposPago.length
    : null

  // Recurrencia: agrupar por comprador. El raw es por canal: ML lo trae en
  // datosCanal.buyer, TN en datosCanal.customerRaw (F2-T3). Sin id de
  // comprador la orden no cuenta — pctRecurrentes queda 0 honesto.
  const buyersMap = new Map()
  for (const o of orders) {
    const id = o.datosCanal?.buyer?.id ?? o.datosCanal?.buyer?.nickname ?? o.datosCanal?.customerRaw?.id
    if (!id) continue
    buyersMap.set(id, (buyersMap.get(id) || 0) + 1)
  }
  const totalBuyers = buyersMap.size
  const recurrentes = Array.from(buyersMap.values()).filter(c => c > 1)
  const pctRecurrentes = totalBuyers > 0 ? recurrentes.length / totalBuyers : 0
  const promedioComprasRecurrentes = recurrentes.length > 0
    ? recurrentes.reduce((a, b) => a + b, 0) / recurrentes.length
    : 0
  const ordenesDeRecurrentes = recurrentes.reduce((a, b) => a + b, 0)
  const pctOrdenesDeRecurrentes = total > 0 ? ordenesDeRecurrentes / total : 0

  return {
    total,
    pagadas: pagadas.length,
    canceladas: canceladas.length,
    totalMonto,
    montoPagadas,
    montoCanceladas,
    ticket,
    conversion,
    tasaCancelacion,
    velocidadPagoHoras,
    totalBuyers,
    recurrentes: recurrentes.length,
    pctRecurrentes,
    promedioComprasRecurrentes,
    pctOrdenesDeRecurrentes,
    ...unidadesVendidas(orders),
    currency: orders[0]?.moneda || 'ARS',
  }
}

// ─── T7-g: las métricas con entrada en el diccionario canónico ──────────────
// docs/diccionario-metricas.md cita `calcKpisVentas` en sus 4 entradas; este
// mapa dice QUÉ campo del resultado es cada métrica canónica. Lo consumen el
// snapshot del FAB (VentasPage publica) y el script de reconciliación
// (recomputa con la MISMA función y compara campo a campo). La paridad con el
// .md está blindada por snapshotMetricas.paridad.test.js con el parser real.
export const METRICAS_DICCIONARIO_VENTAS = {
  'facturado': 'montoPagadas',
  'ordenes-pagadas': 'pagadas',
  'ticket-promedio': 'ticket',
  'tasa-cancelacion': 'tasaCancelacion',
}

// ─── Facturado por publicación (flywheel del Cuadro General, §1.8) ──────────
// Sobre las MISMAS órdenes de la ventana: agrupa el facturado facturable por
// item (refCanal de items[]) para detectar publicaciones que facturaban en el
// período anterior y desaparecieron en el actual — la señal de "quiebre o
// pausa" del eslabón Stock del flywheel, calculada con datos ya cargados.

/**
 * @param {Array} orders — órdenes canónicas
 * @returns {Map<string, { id, titulo, monto, unidades }>}
 */
export function facturadoPorItem(orders) {
  const map = new Map()
  for (const o of orders || []) {
    if (!esFacturable(o)) continue
    for (const it of o.items || []) {
      const id = it.refCanal || null
      if (!id) continue
      const monto = (Number(it.cantidad) || 0) * (Number(it.precioUnitario) || 0)
      const cur = map.get(id) || { id, titulo: it.descripcion || null, monto: 0, unidades: 0 }
      cur.monto += monto
      cur.unidades += Number(it.cantidad) || 0
      if (!cur.titulo && it.descripcion) cur.titulo = it.descripcion
      map.set(id, cur)
    }
  }
  return map
}

/**
 * Serie diaria de facturado facturable para sparklines (V5): un punto por
 * día calendario entre `desde` y `hasta` (días sin ventas = 0, para que la
 * línea no mienta continuidad). Clave de día en hora LOCAL del browser —
 * misma convención visual que los charts de VentasPage.
 *
 * ⚠️ PARED: los días se agrupan y la grilla se arma en **hora LOCAL del
 * browser** (`getFullYear/getMonth/getDate`), no en UTC. Dos consecuencias que
 * conviene tener presentes antes de tocar esto (report XLELDgA6dzUk56DSyRdV):
 *
 *   1. `desde`/`hasta` tienen que venir en pared local — es lo que hace
 *      `resolverPeriodoVentas` (`periodoVentas.js:30-63`) con `new Date(y,m,d)`,
 *      y es como lo llama el único caller real (`CuadroGeneralDashboard.jsx:697`).
 *      **No le pases un `new Date('YYYY-MM-DD')`**: ese literal se parsea como
 *      medianoche UTC y en cualquier huso negativo arranca la grilla un día
 *      antes. Usá `new Date('YYYY-MM-DDT00:00')` o `new Date(y,m,d)`.
 *   2. La pared del browser NO es la misma que la del rollup `__pulso`, que usa
 *      **pared AR fija (UTC-3, sin DST)** por decisión escrita
 *      (`docs/CONTRATO_senales-pulso.md:20`, `functions/dashboardRollups/pulsoRollupCore.js:18-25`).
 *      Para un usuario en AR coinciden; para uno mirando desde otro huso, no.
 *      Es una divergencia declarada, no un descuido — está reportada aparte.
 *
 * @param {Array} orders — órdenes del período (ya filtradas por rango)
 * @param {Date} desde — pared LOCAL
 * @param {Date} hasta — pared LOCAL
 * @returns {Array<{ dia: string, monto: number }>}
 */
export function serieDiariaFacturado(orders, desde, hasta) {
  const mapa = new Map() // dia → { monto, ordenes }
  for (const o of orders || []) {
    if (!esFacturable(o)) continue
    const f = o._fechaCreada || (o.fechaCreada ? new Date(o.fechaCreada) : null)
    if (!f || isNaN(f.getTime())) continue
    const k = claveDiaLocal(f)
    const cur = mapa.get(k) || { monto: 0, ordenes: 0 }
    cur.monto += Number(o.montoTotal) || 0
    cur.ordenes += 1
    mapa.set(k, cur)
  }
  const out = []
  if (!(desde instanceof Date) || !(hasta instanceof Date) || isNaN(desde) || isNaN(hasta)) return out
  const fin = new Date(hasta.getFullYear(), hasta.getMonth(), hasta.getDate())
  for (let d = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate()); d <= fin; d.setDate(d.getDate() + 1)) {
    const v = mapa.get(claveDiaLocal(d))
    out.push({ dia: claveDiaLocal(d), monto: v?.monto || 0, ordenes: v?.ordenes || 0 })
  }
  return out
}

/**
 * Cuenta por día calendario un array de fechas ISO (p.ej. fechaCreada de
 * reclamos) alineado a la MISMA grilla de días que serieDiariaFacturado.
 * @param {string[]} fechasIso
 * @param {Array<{dia: string}>} grillaDias — de serieDiariaFacturado
 * @returns {number[]}
 */
export function contarPorDia(fechasIso, grillaDias) {
  const counts = new Map()
  for (const iso of fechasIso || []) {
    const d = new Date(iso)
    if (isNaN(d.getTime())) continue
    const k = claveDiaLocal(d)
    counts.set(k, (counts.get(k) || 0) + 1)
  }
  return (grillaDias || []).map((g) => counts.get(g.dia) || 0)
}

/**
 * Publicaciones que facturaban en el período ANTERIOR y no vendieron NADA en
 * el actual, ordenadas por lo que facturaban. `shareAnterior` = qué porción
 * del facturado anterior era de esa publicación.
 *
 * @returns {{ desaparecidos: Array<{id,titulo,monto,unidades,shareAnterior}>,
 *             montoDesaparecido: number, montoAnteriorTotal: number }}
 */
export function ventasDesaparecidas(ordersActual, ordersAnterior) {
  const act = facturadoPorItem(ordersActual)
  const ant = facturadoPorItem(ordersAnterior)
  let montoAnteriorTotal = 0
  for (const v of ant.values()) montoAnteriorTotal += v.monto
  const desaparecidos = []
  for (const [id, v] of ant) {
    if (v.monto <= 0) continue
    const ahora = act.get(id)?.monto || 0
    if (ahora === 0) {
      desaparecidos.push({ ...v, shareAnterior: montoAnteriorTotal > 0 ? v.monto / montoAnteriorTotal : 0 })
    }
  }
  desaparecidos.sort((a, b) => b.monto - a.monto)
  return {
    desaparecidos,
    montoDesaparecido: desaparecidos.reduce((s, d) => s + d.monto, 0),
    montoAnteriorTotal,
  }
}
